import { useCallback, useEffect, useRef, useState } from 'react';
import { Row, Space, Typography } from 'antd';
import { ContainerOutlined, FileTextOutlined, PlusOutlined, UndoOutlined } from '@ant-design/icons';
import Button from 'components/new-ui/base/button/Button';
import SearchBar from 'components/new-ui/base/searchbar/SearchBar';
import ArchiveFilter from 'components/new-ui/composed/archive-filter/ArchiveFilter';
import FilesTable, { FileRecord } from 'components/new-ui/composed/table/FilesTable';
import UploadDocuments, { UploadDocumentsRef } from 'components/new-ui/composed/upload-documents/UploadDocuments';
import { BulkUploadUploadFunc } from 'components/new-ui/composed/bulkUpload/BulkUpload';
import Notification from 'components/ui/base/notification/Notification';
import { getFileExtensionFromMime } from 'utils/file-types';
import { validateCustomerFile } from 'utils/validateCustomerFile';
import useGlobalOverlay from 'hooks/useGlobalOverlay';
import usePollPendingSecurityChecks from 'hooks/usePollPendingSecurityChecks';
import { useParams } from 'react-router-dom';
import { DEFAULT_INTERNAL_ERROR_MESSAGE, S3KEY_REGEX } from 'core/utils/constants';
import { getErrorMessage } from 'utils/getErrorMessage';
import {
    createCustomerFile,
    getDocumentDownloadUrl,
    listCustomerFiles,
    getDocumentUploadUrl,
    deleteCustomerFile,
    archiveCustomerFile,
    unarchiveCustomerFile,
} from 'store/customers/client';
import { uploadFile } from 'utils/uploadFile';
import { DocumentResponse, ListCustomerFilesSortParams } from 'store/customers/customer';
import { ListPayload } from 'core/http/response';
import { triggerBrowserDownload } from 'utils/download-utils';
import ConfirmationModal from 'components/new-ui/composed/modal/confirmation-modal/ConfirmationModal';
import AEP5DocumentUploadModal from 'views/customers/components/create-customer-drawer/AEP5DocumentUploadModal';
import { SortDirections, useHandleTableChange } from 'utils/tableHelpers';
import Alert from 'components/new-ui/base/alert/Alert';
import { usePartyContext } from '../../PartyProvider';
import { EMPTY } from '../../utils';

const POLL_INTERVAL_MS = 10_000;

type FileActionType = 'delete' | 'archive' | 'unarchive';

type PendingFileAction = {
    type: FileActionType;
    file: FileRecord;
};

const FILE_ACTION_CONFIG: Record<
    FileActionType,
    { execute: (customerId: string, fileId: string) => Promise<unknown>; successMessage: string }
> = {
    delete: { execute: deleteCustomerFile, successMessage: 'File successfully deleted.' },
    archive: { execute: archiveCustomerFile, successMessage: 'File successfully archived.' },
    unarchive: { execute: unarchiveCustomerFile, successMessage: 'File successfully unarchived.' },
};

const CustomerFilesTab = () => {
    const { customer, viewOnly, canUpdate, configurationStatus, refreshCustomer } = usePartyContext();
    const { customerId } = useParams();
    const { showGlobalOverlay, hideGlobalOverlay } = useGlobalOverlay();
    const [isFetching, setIsFetching] = useState(false);
    const [files, setFiles] = useState<ListPayload<DocumentResponse>>();
    const [lastFetchedAt, setLastFetchedAt] = useState<number | null>(null);
    const [pendingAction, setPendingAction] = useState<PendingFileAction | null>(null);
    const [isProcessingAction, setIsProcessingAction] = useState(false);
    const [showAEP5UploadModal, setShowAEP5UploadModal] = useState(false);
    const [showReplaceAEP5Confirmation, setShowReplaceAEP5Confirmation] = useState(false);

    const uploaderRef = useRef<UploadDocumentsRef>(null);
    const canTriggerUpdate = !viewOnly && canUpdate;
    const aep5Required = configurationStatus.aep5.required;
    const hasAEP5Document = configurationStatus.aep5.complete;
    const showAEP5DocumentWarning = aep5Required && !hasAEP5Document;

    const { query, page, pageSize, filters, sort, onSearch, onTableChange, setFilter } = useHandleTableChange({
        filterKeys: ['archived'] as const,
    });

    const refreshCustomerFiles = useCallback(async () => {
        if (!customerId) return;

        setIsFetching(true);
        try {
            const response = await listCustomerFiles(customerId, {
                page,
                size: pageSize,
                fileName: query.debounced ?? undefined,
                archived: filters?.archived === 'true',
                sortDirection: sort.sortDirection as SortDirections,
                fileSortParameter: sort.sort as ListCustomerFilesSortParams,
            });
            setFiles(response);
            setLastFetchedAt(Date.now());
        } catch {
        } finally {
            setIsFetching(false);
        }
    }, [customerId, filters?.archived, page, pageSize, query.debounced, sort.sort, sort.sortDirection]);

    useEffect(() => {
        refreshCustomerFiles();
    }, [refreshCustomerFiles]);

    // Poll files on interval so we can update the UI based on securityCheckStatus for each file
    usePollPendingSecurityChecks(files?.list, refreshCustomerFiles, lastFetchedAt, {
        enabled: !!customerId,
        intervalMs: POLL_INTERVAL_MS,
    });

    const handleUploadDocumentsClick = () => {
        uploaderRef.current?.triggerUpload();
    };

    // A party keeps a single AEP5 document, so uploading another one replaces what is already there
    const handleUploadAEP5Click = () => {
        if (hasAEP5Document) {
            setShowReplaceAEP5Confirmation(true);
            return;
        }

        setShowAEP5UploadModal(true);
    };

    const handleAEP5Uploaded = () => {
        refreshCustomerFiles();
        // The party carries the AEP5 status the warning above and the tab badges are based on
        refreshCustomer();
    };

    const handleUpload = async (options: Parameters<BulkUploadUploadFunc>[0]) => {
        showGlobalOverlay({ type: 'LoadingOverlay' });
        try {
            if (!customerId) {
                console.error('Customer id is missing');
                throw new Error(DEFAULT_INTERNAL_ERROR_MESSAGE);
            }

            const file = options.file as File;

            if (!file) {
                console.error('File is missing');
                throw new Error(DEFAULT_INTERNAL_ERROR_MESSAGE);
            }

            //1. Get the upload presigned URL
            const { preSignedUrl } = await getDocumentUploadUrl(customerId, file.name, file.size);
            await uploadFile(file, preSignedUrl);

            const match = preSignedUrl.match(S3KEY_REGEX);
            const s3key = match?.[1];

            if (!s3key) {
                throw new Error('Could not extract s3key from presigned url');
            }

            //2. Create the customer file
            await createCustomerFile(customerId, {
                s3key,
                fileName: file.name,
                fileSize: file.size,
            });

            refreshCustomerFiles();

            Notification({
                type: 'success',
                placement: 'top',
                messageTitle: 'File uploaded',
                description: `${file.name} was uploaded successfully.`,
            });
        } catch (err) {
            const errorMessage = getErrorMessage(err) || DEFAULT_INTERNAL_ERROR_MESSAGE;
            Notification({
                type: 'error',
                placement: 'top',
                messageTitle: 'Upload failed',
                description: errorMessage,
            });
        } finally {
            hideGlobalOverlay();
        }
    };

    const handlePreview = async (file: FileRecord | undefined) => {
        if (!file || !customerId) {
            return Notification({
                type: 'error',
                placement: 'top',
                messageTitle: 'Preview Error',
                description: 'Missing file or party data.',
            });
        }

        showGlobalOverlay({ type: 'LoadingOverlay' });
        try {
            const filePreview = await getDocumentDownloadUrl(customerId, file.id);
            window.open(filePreview.preSignedUrl);
        } catch (error) {
            const errorMessage = getErrorMessage(error);
            return Notification({
                type: 'error',
                placement: 'top',
                messageTitle: 'Preview Error',
                description: errorMessage || 'Could not generate preview url.',
            });
        } finally {
            hideGlobalOverlay();
        }
    };

    const handleDownload = async (file: FileRecord | undefined) => {
        if (!file || !customerId) {
            console.error('Missing file or party data.');
            return Notification({
                type: 'error',
                placement: 'top',
                messageTitle: 'Download Error',
                description: DEFAULT_INTERNAL_ERROR_MESSAGE,
            });
        }

        showGlobalOverlay({ type: 'LoadingOverlay' });
        try {
            const { preSignedUrl } = await getDocumentDownloadUrl(customerId, file.id);
            const response = await fetch(preSignedUrl);
            const blob = await response.blob();
            triggerBrowserDownload(blob, file?.filename || 'document');
        } catch (err) {
            console.error(err);
            return Notification({
                type: 'error',
                placement: 'top',
                messageTitle: 'Download Error',
                description: 'Could not download document. Please try again.',
            });
        } finally {
            hideGlobalOverlay();
        }
    };

    const requestFileAction = (type: FileActionType) => (file: FileRecord | undefined) => {
        if (!file) return;
        setPendingAction({ type, file });
    };

    const onRequestDelete = requestFileAction('delete');
    const onRequestArchive = requestFileAction('archive');
    const onRequestUnarchive = requestFileAction('unarchive');

    const clearPendingAction = () => {
        setPendingAction(null);
        setIsProcessingAction(false);
    };

    const handleConfirmPendingAction = async () => {
        if (!customerId || !pendingAction) {
            return;
        }

        const { execute, successMessage } = FILE_ACTION_CONFIG[pendingAction.type];

        showGlobalOverlay({ type: 'LoadingOverlay' });
        setIsProcessingAction(true);
        try {
            await execute(customerId, pendingAction.file.id);

            Notification({
                type: 'success',
                placement: 'top',
                messageTitle: 'Operation Successful',
                description: successMessage,
            });

            refreshCustomerFiles();
        } catch (error) {
            Notification({
                type: 'error',
                placement: 'top',
                messageTitle: 'Operation Failed',
                description: DEFAULT_INTERNAL_ERROR_MESSAGE,
            });
        } finally {
            hideGlobalOverlay();
            clearPendingAction();
        }
    };

    return (
        <>
            <Space size={24} direction="vertical" style={{ width: '100%', marginTop: 6 }}>
                {showAEP5DocumentWarning && <Alert message="AEP5 Document not yet uploaded." type="warning" showIcon />}

                <Row justify="space-between">
                    <div>
                        <SearchBar inputPlaceholder="Search files" value={query.current} onChange={onSearch} />
                    </div>
                    <Space size={16}>
                        <ArchiveFilter
                            value={filters?.archived === 'true' ? 'archived' : 'active'}
                            onChange={(value) => setFilter('archived', value === 'active' ? 'false' : 'true')}
                        />
                        {canTriggerUpdate && aep5Required && (
                            <Button onClick={handleUploadAEP5Click} icon={<FileTextOutlined />}>
                                {hasAEP5Document ? 'Replace AEP5 Document' : 'Upload AEP5 Document'}
                            </Button>
                        )}
                        {canTriggerUpdate && (
                            <Button onClick={handleUploadDocumentsClick} type="primary" icon={<PlusOutlined />}>
                                Upload File
                            </Button>
                        )}
                    </Space>
                </Row>

                {canTriggerUpdate && (
                    <UploadDocuments
                        ref={uploaderRef}
                        //  multiple
                        // Note: There is some issue with the BE where when we create multiple documents at the same time
                        // it overrides the results. Enable `multiple` when the issue is fixed
                        showUploadList={false}
                        beforeUpload={validateCustomerFile}
                        customRequest={handleUpload}
                    />
                )}

                <FilesTable
                    loading={isFetching}
                    dataSource={files?.list?.map((file) => ({
                        id: file.id,
                        filename: file.fileName,
                        fileType: getFileExtensionFromMime(file.contentType) ?? EMPTY,
                        uploadedBy: file?.creator?.fullName ?? EMPTY,
                        createdAt: file.createdAt,
                        securityCheckStatus: file.securityChecksStatus,
                        archived: file.archived,
                        activeFrom: file.activeFrom,
                        activeTo: file.activeTo,
                    }))}
                    onChange={onTableChange}
                    pagination={{
                        current: (files?.pageNumber ?? 0) + 1,
                        total: files?.total,
                        pageSize: files?.pageSize ?? pageSize,
                        showSizeChanger: false,
                        position: ['bottomCenter'],
                        hideOnSinglePage: true,
                    }}
                    actions={{
                        preview: handlePreview,
                        ...(canTriggerUpdate && {
                            download: handleDownload,
                            delete: onRequestDelete,
                            archive: onRequestArchive,
                            unarchive: onRequestUnarchive,
                        }),
                    }}
                />
            </Space>

            <ConfirmationModal
                type="error"
                title="Delete File?"
                confirmButtonText="Delete"
                content={
                    <Typography.Text>
                        The file will be deleted and all information associated with it will be lost
                    </Typography.Text>
                }
                open={pendingAction?.type === 'delete'}
                confirmationButtonLoading={isProcessingAction}
                onCancel={clearPendingAction}
                onConfirm={handleConfirmPendingAction}
            />

            <ConfirmationModal
                open={pendingAction?.type === 'archive'}
                width={420}
                icon={<ContainerOutlined />}
                title="Archive File?"
                confirmButtonText="Archive"
                confirmationButtonLoading={isProcessingAction}
                onConfirm={handleConfirmPendingAction}
                onCancel={clearPendingAction}
                content={<Typography.Text>The file will be added to the archive.</Typography.Text>}
            />

            <ConfirmationModal
                open={showReplaceAEP5Confirmation}
                width={420}
                icon={<FileTextOutlined />}
                title="Replace AEP5 Document?"
                confirmButtonText="Replace"
                onConfirm={() => {
                    setShowReplaceAEP5Confirmation(false);
                    setShowAEP5UploadModal(true);
                }}
                onCancel={() => setShowReplaceAEP5Confirmation(false)}
                content={
                    <Typography.Text>
                        This party already has an AEP5 document. The one you upload next will be used instead.
                    </Typography.Text>
                }
            />

            <AEP5DocumentUploadModal
                open={showAEP5UploadModal}
                customerId={customerId ?? ''}
                onUploaded={handleAEP5Uploaded}
                onClose={() => setShowAEP5UploadModal(false)}
            />

            <ConfirmationModal
                open={pendingAction?.type === 'unarchive'}
                width={420}
                icon={<UndoOutlined />}
                title="Unarchive File?"
                confirmButtonText="Unarchive"
                confirmationButtonLoading={isProcessingAction}
                onConfirm={handleConfirmPendingAction}
                onCancel={clearPendingAction}
                content={<Typography.Text>The file will be restored from the archive.</Typography.Text>}
            />
        </>
    );
};

export default CustomerFilesTab;
